import React, { useEffect, useRef } from 'react';
import L from 'leaflet';

const cssVar = (name, fallback) => {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch { return fallback; }
};

// Leaflet map (React 19 safe). Shows:
//  - planned road route (origin -> destination) as a dashed line
//  - origin + destination pins
//  - traveled path (live breadcrumbs)
//  - current position marker (auto-follows unless a route is shown)
export const MapView = ({ position, path = [], route = [], origin, destination, height = '100%', interactive = true, testid = 'map-view' }) => {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const accCircleRef = useRef(null);
  const lineRef = useRef(null);
  const routeRef = useRef(null);
  const originRef = useRef(null);
  const destRef = useRef(null);
  const fittedRef = useRef(false);
  const hasRoute = route && route.length > 1;

  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const start = position || origin || destination || { latitude: 14.5995, longitude: 120.9842 };
    const map = L.map(elRef.current, {
      zoomControl: interactive, dragging: interactive, scrollWheelZoom: interactive, attributionControl: true,
    }).setView([start.latitude, start.longitude], 16);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '&copy; OpenStreetMap' }).addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => {
      map.remove();
      mapRef.current = null;
      // reset layer refs so they re-create on the fresh map (handles StrictMode remount)
      markerRef.current = null;
      accCircleRef.current = null;
      lineRef.current = null;
      routeRef.current = null;
      originRef.current = null;
      destRef.current = null;
      fittedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Planned route + origin/destination pins
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const primary = cssVar('--primary', '#0F766E');

    if (hasRoute) {
      const pts = route.map((p) => [p.latitude, p.longitude]);
      if (routeRef.current) routeRef.current.setLatLngs(pts);
      else routeRef.current = L.polyline(pts, { color: primary, weight: 5, opacity: 0.55, dashArray: '10,10', lineCap: 'round' }).addTo(map);
    }

    if (origin && origin.latitude != null && !originRef.current) {
      const icon = L.divIcon({ className: 'ss-origin', html: `<div style="width:14px;height:14px;border-radius:50%;background:#fff;border:4px solid ${primary};box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>`, iconSize: [14, 14], iconAnchor: [7, 7] });
      originRef.current = L.marker([origin.latitude, origin.longitude], { icon }).addTo(map).bindTooltip('Pickup', { direction: 'top' });
    }
    if (destination && destination.latitude != null && !destRef.current) {
      const icon = L.divIcon({ className: 'ss-dest', html: `<div style="transform:translate(-2px,-24px);font-size:24px;line-height:1">📍</div>`, iconSize: [24, 24], iconAnchor: [0, 0] });
      destRef.current = L.marker([destination.latitude, destination.longitude], { icon }).addTo(map).bindTooltip('Destination', { direction: 'top' });
    }

    // Fit to show the whole journey once
    if ((hasRoute || destination) && !fittedRef.current) {
      const layers = [];
      if (routeRef.current) layers.push(routeRef.current);
      if (originRef.current) layers.push(originRef.current);
      if (destRef.current) layers.push(destRef.current);
      if (position) layers.push(L.marker([position.latitude, position.longitude]));
      if (layers.length) {
        try {
          const group = L.featureGroup(layers);
          map.fitBounds(group.getBounds().pad(0.2));
          fittedRef.current = true;
        } catch (err) { console.warn('Map fitBounds failed:', err); }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, origin, destination]);

  // Current position marker
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !position) return;
    const primary = cssVar('--primary', '#0F766E');
    const latlng = [position.latitude, position.longitude];
    if (!markerRef.current) {
      const icon = L.divIcon({ className: 'ss-marker', html: `<div style="width:20px;height:20px;border-radius:50%;background:${primary};border:3px solid #fff;box-shadow:0 0 0 4px rgba(15,118,110,0.25)"></div>`, iconSize: [20, 20], iconAnchor: [10, 10] });
      markerRef.current = L.marker(latlng, { icon }).addTo(map);
    } else {
      markerRef.current.setLatLng(latlng);
    }
    // Accuracy halo: shows how precise the current fix is (radius = metres).
    const acc = position.accuracy;
    if (acc != null && acc > 0 && acc <= 2000) {
      if (!accCircleRef.current) {
        accCircleRef.current = L.circle(latlng, { radius: acc, color: primary, weight: 1, opacity: 0.4, fillColor: primary, fillOpacity: 0.1 }).addTo(map);
      } else {
        accCircleRef.current.setLatLng(latlng);
        accCircleRef.current.setRadius(acc);
      }
    } else if (accCircleRef.current) {
      accCircleRef.current.remove();
      accCircleRef.current = null;
    }
    // If no route is shown, follow the rider. If a route is shown, keep the full view.
    if (!hasRoute && !destination) map.panTo(latlng, { animate: true });
  }, [position, hasRoute, destination]);

  // Traveled path (breadcrumbs)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const pts = path.filter(Boolean).map((p) => [p.latitude, p.longitude]);
    if (pts.length > 1) {
      if (lineRef.current) lineRef.current.setLatLngs(pts);
      else lineRef.current = L.polyline(pts, { color: '#14B8A6', weight: 4, opacity: 0.85 }).addTo(map);
    }
  }, [path]);

  return <div ref={elRef} data-testid={testid} style={{ height, width: '100%', borderRadius: 'var(--radius)', overflow: 'hidden' }} />;
};
